import React from 'react';
import { Plus, Trash2 } from 'lucide-react';

import type { Budget, BudgetLine } from '../../services/api/assistants';

interface Props {
  budget: Budget;
  onChange: (budget: Budget) => void;
  readOnly?: boolean;
}

// Une ligne ajoutée à la main part non sourcée : c'est à l'utilisateur de
// renseigner d'où vient le chiffre, comme l'exige la règle « sans inventer ».
const LIGNE_VIDE: BudgetLine = {
  designation: '',
  code: 'Non documenté',
  unite: 'forfait',
  quantite: 1,
  cout_unitaire: 0,
  montant: 0,
  source: 'À renseigner',
  commentaire: '',
};

/**
 * Recalcule montants, sous-totaux et total à partir des quantités et coûts.
 *
 * Le recalcul est fait ici plutôt que côté serveur pour que la grille réagisse
 * immédiatement à la frappe : c'est l'argument central de la démonstration.
 */
function recalculer(budget: Budget): Budget {
  // Une production incomplete arrive : le modele peut rendre un budget sans
  // rubriques, ou une rubrique sans lignes, et la grille plantait alors sur
  // « Cannot read properties of undefined ». L'ecran entier disparaissait,
  // sans rien dire de ce qui manquait.
  const rubriques = (budget.rubriques || []).map((rubrique) => {
    const lignes = (rubrique.lignes || []).map((ligne) => ({
      ...ligne,
      montant: Math.round(ligne.quantite * ligne.cout_unitaire),
    }));
    return {
      ...rubrique,
      lignes,
      sous_total: lignes.reduce((somme, ligne) => somme + ligne.montant, 0),
    };
  });

  return {
    ...budget,
    rubriques,
    total: rubriques.reduce((somme, rubrique) => somme + rubrique.sous_total, 0),
  };
}

/** Une ligne dont le coût n'a pas de source identifiée reste à compléter. */
function estASourcer(source: string): boolean {
  const normalise = (source || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
  return !normalise || normalise.startsWith('a renseigner') || normalise.startsWith('non documente');
}

const BudgetGrid: React.FC<Props> = ({ budget, onChange, readOnly = false }) => {
  const formatter = new Intl.NumberFormat('fr-FR');
  const montant = (valeur: number) => `${formatter.format(valeur)} ${budget.devise}`;

  const lignesASourcer = (budget.rubriques || []).reduce(
    (total, rubrique) =>
      total + (rubrique.lignes || []).filter((l) => estASourcer(l.source)).length,
    0,
  );

  const modifierLigne = (
    indexRubrique: number,
    indexLigne: number,
    champ: keyof BudgetLine,
    valeur: string,
  ) => {
    const suivant = structuredClone(budget) as Budget;
    const ligne = suivant.rubriques[indexRubrique].lignes[indexLigne];

    if (champ === 'quantite' || champ === 'cout_unitaire') {
      const nombre = parseFloat(valeur.replace(',', '.'));
      (ligne[champ] as number) = Number.isFinite(nombre) ? nombre : 0;
    } else {
      (ligne[champ] as string) = valeur;
    }

    onChange(recalculer(suivant));
  };

  const supprimerLigne = (indexRubrique: number, indexLigne: number) => {
    const suivant = structuredClone(budget) as Budget;
    suivant.rubriques[indexRubrique].lignes.splice(indexLigne, 1);
    onChange(recalculer(suivant));
  };

  const ajouterLigne = (indexRubrique: number) => {
    const suivant = structuredClone(budget) as Budget;
    suivant.rubriques[indexRubrique].lignes.push({ ...LIGNE_VIDE });
    onChange(recalculer(suivant));
  };

  const champ = (valeur: string | number, onEdit: (v: string) => void, aligneDroite = false) => (
    <input
      type="text"
      value={valeur}
      disabled={readOnly}
      onChange={(evenement) => onEdit(evenement.target.value)}
      className={`w-full bg-transparent px-2 py-1 rounded border border-transparent
        hover:border-[#DDE2DD] focus:border-eg-accent focus:bg-eg-blanc focus:outline-none
        disabled:hover:border-transparent ${aligneDroite ? 'text-right' : ''}`}
    />
  );

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-lg font-semibold text-eg-encre">{budget.titre}</h3>
        <span className="text-sm text-eg-gris">
          Montants en {budget.devise}
          {budget.taux_change_gnf_usd
            ? ` · 1 USD = ${formatter.format(budget.taux_change_gnf_usd)} GNF`
            : ''}
        </span>
      </div>

      {lignesASourcer > 0 && (
        <div className="rounded-md bg-amber-100 border border-amber-300 px-4 py-3 text-sm text-eg-encre">
          <strong>{lignesASourcer} ligne{lignesASourcer > 1 ? 's' : ''} à compléter.</strong>{' '}
          Le coût n'a pas de source identifiée et vaut zéro. Renseignez-le depuis le barème
          officiel avant de soumettre le budget — les lignes concernées sont surlignées.
        </div>
      )}

      {budget.hypotheses?.length > 0 && (
        <div className="rounded-md bg-eg-alerte/10 border border-eg-alerte/30 p-4">
          <p className="text-sm font-medium text-eg-encre mb-2">Hypothèses retenues</p>
          <ul className="text-sm text-eg-alerte space-y-1 list-disc list-inside">
            {budget.hypotheses.map((hypothese, index) => (
              <li key={index}>{hypothese}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-eg-bord">
        <table className="w-full text-sm">
          <thead className="bg-eg-papier text-eg-gris">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-24">Code</th>
              <th className="text-left px-3 py-2 font-medium">Désignation</th>
              <th className="text-left px-3 py-2 font-medium w-28">Unité</th>
              <th className="text-right px-3 py-2 font-medium w-24">Quantité</th>
              <th className="text-right px-3 py-2 font-medium w-36">Coût unitaire</th>
              <th className="text-right px-3 py-2 font-medium w-40">Montant</th>
              <th className="text-left px-3 py-2 font-medium w-48">Source</th>
              <th className="text-left px-3 py-2 font-medium">Commentaire</th>
              {!readOnly && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {/* Une grille vide sans explication laisse l'agent devant un
                tableau muet, sans savoir si la plateforme a echoue ou s'il a
                mal demande. On nomme ce qui manque. */}
            {(budget.rubriques || []).length === 0 && (
              <tr>
                <td colSpan={readOnly ? 8 : 9} className="px-3 py-6 text-center text-eg-gris">
                  Cette production ne contient aucune rubrique budgetaire. La
                  generation s'est probablement interrompue : relancez la
                  demande.
                </td>
              </tr>
            )}
            {(budget.rubriques || []).map((rubrique, indexRubrique) => (
              <React.Fragment key={indexRubrique}>
                <tr className="bg-eg-papier">
                  <td colSpan={readOnly ? 8 : 9} className="px-3 py-2 font-semibold text-eg-encre">
                    {rubrique.nom}
                  </td>
                </tr>

                {(rubrique.lignes || []).map((ligne, indexLigne) => (
                  <tr
                    key={indexLigne}
                    className={`border-t border-eg-bordure hover:bg-eg-papier ${
                      estASourcer(ligne.source) ? 'bg-eg-alerte/10' : ''
                    }`}
                  >
                    <td className="px-1">
                      {champ(ligne.code, (v) => modifierLigne(indexRubrique, indexLigne, 'code', v))}
                    </td>
                    <td className="px-1">
                      {champ(ligne.designation, (v) => modifierLigne(indexRubrique, indexLigne, 'designation', v))}
                    </td>
                    <td className="px-1">
                      {champ(ligne.unite, (v) => modifierLigne(indexRubrique, indexLigne, 'unite', v))}
                    </td>
                    <td className="px-1">
                      {champ(ligne.quantite, (v) => modifierLigne(indexRubrique, indexLigne, 'quantite', v), true)}
                    </td>
                    <td className="px-1">
                      {champ(ligne.cout_unitaire, (v) => modifierLigne(indexRubrique, indexLigne, 'cout_unitaire', v), true)}
                    </td>
                    <td className="px-3 py-1 text-right font-medium text-eg-encre tabular-nums">
                      {montant(ligne.montant)}
                    </td>
                    <td className="px-1">
                      {champ(ligne.source, (v) => modifierLigne(indexRubrique, indexLigne, 'source', v))}
                    </td>
                    <td className="px-1">
                      {champ(ligne.commentaire, (v) => modifierLigne(indexRubrique, indexLigne, 'commentaire', v))}
                    </td>
                    {!readOnly && (
                      <td className="px-1 text-center">
                        <button
                          type="button"
                          onClick={() => supprimerLigne(indexRubrique, indexLigne)}
                          className="text-eg-bord hover:text-red-500 transition-colors"
                          title="Supprimer la ligne"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}

                <tr className="border-t border-eg-bord bg-eg-papier">
                  <td colSpan={5} className="px-3 py-2 text-right font-medium text-eg-brun">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => ajouterLigne(indexRubrique)}
                        className="float-left inline-flex items-center gap-1 text-eg-accent hover:text-eg-brun font-normal"
                      >
                        <Plus className="w-4 h-4" /> Ajouter une ligne
                      </button>
                    )}
                    Sous-total {rubrique.nom}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-eg-encre tabular-nums">
                    {montant(rubrique.sous_total)}
                  </td>
                  <td colSpan={readOnly ? 2 : 3} />
                </tr>
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[#DDE2DD] bg-eg-ombre text-eg-blanc">
              <td colSpan={5} className="px-3 py-3 text-right font-semibold">TOTAL GÉNÉRAL</td>
              <td className="px-3 py-3 text-right text-lg font-bold tabular-nums">
                {montant(budget.total)}
              </td>
              <td colSpan={readOnly ? 1 : 2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default BudgetGrid;
